import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { glyphFor, HelpIcon, HelpTile, ORIGINAL_ICONS, useGlyphs } from './HelpIcon';

/**
 * Choosing a collection's icon from the whole Iconsax Bold set.
 *
 * It used to be a select of the seven names the apps had imported, so any
 * new topic had to reuse one of them. The apps now carry every glyph, so the
 * choice is all 993, searchable by name and browsable by Iconsax's own
 * categories.
 */

/** The seven the help centre started with, listed first. */
const ORIGINALS = 'Help centre originals';

/** "ShieldTick" → "shield tick", so a search for "shield t" finds it. */
function words(name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
}

export const IconPicker: React.FC<{
  value: string;
  accent: string;
  onChange: (icon: string) => void;
}> = ({ value, accent, onChange }) => {
  const glyphs = useGlyphs();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');

  const sections = useMemo(() => {
    if (!glyphs) return [];
    const q = query.trim().toLowerCase();
    const all = [{ name: ORIGINALS, icons: ORIGINAL_ICONS }, ...glyphs.ICONSAX_CATEGORIES];
    const chosen = all.filter((c) => !category || c.name === category);
    if (!q) return chosen;
    const hits = new Set<string>();
    for (const c of chosen) {
      const categoryMatches = c.name.toLowerCase().includes(q);
      for (const icon of c.icons) {
        if (categoryMatches || icon.toLowerCase().includes(q) || words(icon).includes(q)) {
          hits.add(icon);
        }
      }
    }
    // One flat, alphabetical list: the same icon can sit in two categories.
    return [{ name: `${hits.size} match${hits.size === 1 ? '' : 'es'}`, icons: Array.from(hits).sort() }];
  }, [glyphs, query, category]);

  const unknown = !!glyphs && !glyphFor(glyphs, value);

  return (
    <div>
      <span style={labelStyle}>Icon</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <HelpTile icon={value} accent={accent} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--neutral-800)' }}>{value}</div>
          {unknown ? (
            <div style={{ fontSize: 12, color: 'var(--danger-600)' }}>
              Not an Iconsax icon — the apps show a book instead.
            </div>
          ) : null}
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} style={linkButtonStyle}>
          {open ? 'Done' : 'Change'}
        </button>
      </div>

      {open ? (
        <div
          style={{
            marginTop: 10,
            border: '1px solid var(--neutral-300)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 8,
              padding: 8,
              borderBottom: '1px solid var(--neutral-200)',
              alignItems: 'center',
            }}
          >
            <Search size={15} color="var(--neutral-500)" style={{ flexShrink: 0, marginLeft: 4 }} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search 993 icons…"
              aria-label="Search icons"
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                fontSize: 13,
                fontFamily: 'inherit',
                background: 'transparent',
              }}
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="Icon category"
              style={{
                maxWidth: 150,
                padding: '6px 8px',
                border: '1px solid var(--neutral-300)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                fontFamily: 'inherit',
                background: 'var(--neutral-100)',
              }}
            >
              <option value="">All categories</option>
              <option value={ORIGINALS}>{ORIGINALS}</option>
              {glyphs?.ICONSAX_CATEGORIES.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ maxHeight: 280, overflowY: 'auto', padding: '4px 8px 8px' }}>
            {!glyphs ? (
              <p style={emptyStyle}>Loading icons…</p>
            ) : sections.every((s) => s.icons.length === 0) ? (
              <p style={emptyStyle}>No icon matches “{query.trim()}”.</p>
            ) : (
              sections.map((section) => (
                <div key={section.name}>
                  <div style={sectionStyle}>{section.name}</div>
                  <div
                    role="listbox"
                    aria-label={section.name}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))',
                      gap: 4,
                    }}
                  >
                    {section.icons.map((icon) => {
                      const selected = icon === value;
                      return (
                        <button
                          key={icon}
                          type="button"
                          role="option"
                          aria-selected={selected}
                          aria-label={icon}
                          title={icon}
                          onClick={() => onChange(icon)}
                          style={{
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${selected ? 'var(--blue-700)' : 'transparent'}`,
                            borderRadius: 'var(--radius-sm)',
                            background: selected ? 'var(--blue-100)' : 'transparent',
                            color: selected ? 'var(--blue-800)' : 'var(--neutral-700)',
                            cursor: 'pointer',
                          }}
                        >
                          <HelpIcon name={icon} size={20} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
          <p
            style={{
              margin: 0,
              padding: '8px 12px',
              borderTop: '1px solid var(--neutral-200)',
              fontSize: 11,
              color: 'var(--neutral-500)',
            }}
          >
            App versions released before the full icon set show a book for icons they don’t have.
          </p>
        </div>
      ) : null}
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--neutral-700)',
  marginBottom: 6,
};

const linkButtonStyle: React.CSSProperties = {
  border: '1px solid var(--neutral-300)',
  background: 'var(--neutral-100)',
  borderRadius: 'var(--radius-md)',
  padding: '6px 12px',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'inherit',
  color: 'var(--neutral-800)',
  cursor: 'pointer',
};

const sectionStyle: React.CSSProperties = {
  padding: '10px 2px 6px',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--neutral-500)',
};

const emptyStyle: React.CSSProperties = {
  margin: 0,
  padding: '16px 4px',
  fontSize: 13,
  color: 'var(--neutral-500)',
};
